// Credential scrub for the app logger's Datadog transmit path. Walks payloads
// the way the Datadog SDK serializes them (`toJSON` first), so an AxiosError's
// `config.headers` is covered. It is NOT a PHI scrub: a logged AxiosError's
// `config.data`/`params` are omitted (not a bare `response.config`'s), and an
// error's `message`/`stack` inside context are kept, so keep `sanitizeError()`.
import { isAxiosError } from './sanitize-error';

export const REDACTED = '[REDACTED]';
export const TRUNCATED = '[TRUNCATED]';
export const OMITTED = '[OMITTED]';

/** Counts only, never values — safe to stamp on the log record. */
export interface ScrubStats {
  redacted: number;
  omitted: number;
  truncated: number;
}

export const createScrubStats = (): ScrubStats => ({
  redacted: 0,
  omitted: 0,
  truncated: 0,
});

// Keys are compared with case, `-` and `_` removed, so `accessToken`,
// `access_token` and `Access-Token` are one key.
const CREDENTIAL_KEYS = new Set([
  'authorization',
  'proxyauthorization',
  'cookie',
  'setcookie',
  'apikey',
  'xapikey',
  'xamzsignature',
  'xamzcredential',
  'clientassertion',
]);
const CREDENTIAL_KEY_SUFFIXES = ['token', 'secret', 'password'];

export const isCredentialKey = (key: string): boolean => {
  const normalized = key.toLowerCase().replace(/[-_]/g, '');
  return (
    CREDENTIAL_KEYS.has(normalized) ||
    CREDENTIAL_KEY_SUFFIXES.some((suffix) => normalized.endsWith(suffix))
  );
};

// A boolean or number cannot be a credential: `hasToken: true` is the PHI-safe
// shape a call site chose on purpose and must survive.
const canHoldCredential = (value: unknown): boolean =>
  value != null && typeof value !== 'boolean' && typeof value !== 'number';

// `Bearer` takes a token-shaped value (8+ chars with a digit or punctuation) so
// "the bearer of" survives; `Basic` only counts after an `Authorization` label.
const BEARER_PATTERN =
  /\b(Bearer)\s+(?=[A-Za-z0-9\-._~+/=]*[0-9\-._~+/=])[A-Za-z0-9\-._~+/=]{8,}/gi;
const BASIC_PATTERN =
  /(authorization["']?\s*[:=]\s*(?:["']\s*)?Basic)\s+[A-Za-z0-9+/]+={0,2}/gi;
// A JWT/JWE starts a token run (base64url of `{"`), may carry `=` padding and
// has at least three dot-joined segments. It may follow `name=` or a
// percent-encoded byte. No nested quantifiers: linear.
const JWT_PATTERN =
  /(^|[^A-Za-z0-9_-]|%[0-9A-Fa-f]{2})eyJ[A-Za-z0-9_-]{4,}=*\.[A-Za-z0-9_-]{4,}=*(?:\.[A-Za-z0-9_=-]*)+/g;
const CREDENTIAL_QUERY_PARAM_PATTERN =
  /([?&#](?:access_token|id_token|id_token_hint|refresh_token|session_token|token|code|state|client_secret|password|x-amz-signature|x-amz-credential|x-amz-security-token|signature|sig)=)[^&#\s"'<>]*/gi;

export function scrubCredentialString(value: string, stats?: ScrubStats): string {
  const hit = (replacement: string): string => {
    if (stats) stats.redacted += 1;
    return replacement;
  };
  let out = value;
  if (/bearer/i.test(out)) {
    out = out.replace(BEARER_PATTERN, (_, scheme: string) =>
      hit(`${scheme} ${REDACTED}`)
    );
  }
  if (/authorization/i.test(out)) {
    out = out.replace(BASIC_PATTERN, (_, label: string) => hit(`${label} ${REDACTED}`));
  }
  if (out.includes('eyJ')) {
    out = out.replace(JWT_PATTERN, (_, lead: string) => hit(`${lead}${REDACTED}`));
  }
  if (out.includes('=')) {
    out = out.replace(CREDENTIAL_QUERY_PARAM_PATTERN, (_, param: string) =>
      hit(`${param}${REDACTED}`)
    );
  }
  return out;
}

// Beyond either budget a node is replaced, never forwarded unscrubbed.
const MAX_DEPTH = 20;
const MAX_NODES = 5_000;

// A failed request's body and params can carry PHI; nothing needs them in logs.
const AXIOS_CONFIG_OMITTED_KEYS = new Set(['data', 'params']);

const WALKABLE_TAGS = new Set(['Object', 'Array', 'Error']);

const typeTagOf = (value: object): string =>
  Object.prototype.toString.call(value).slice(8, -1);

// The SDK sends every other object type as its `[Tag]` string; doing the same
// here means a Map or Headers never reaches it with its contents.
const collapseUnwalkable = (value: object): unknown => {
  if (typeof Event !== 'undefined' && value instanceof Event) {
    return { isTrusted: value.isTrusted };
  }
  return `[${typeTagOf(value)}]`;
};

const applyToJSON = (value: unknown): unknown => {
  const candidate = value as { toJSON?: unknown } | null;
  if (candidate && typeof candidate.toJSON === 'function') {
    try {
      return (candidate.toJSON as () => unknown)();
    } catch {
      return value;
    }
  }
  return value;
};

export function scrubCredentials(
  value: unknown,
  stats: ScrubStats = createScrubStats()
): unknown {
  const copies = new WeakMap<object, unknown>();
  let nodes = 0;

  // Cycles are copied, not broken, so a circular payload still fails to
  // serialize exactly as before.
  const walk = (node: unknown, depth: number, omitKeys?: Set<string>): unknown => {
    if (typeof node === 'string') return scrubCredentialString(node, stats);
    if (!node || typeof node !== 'object') return node;

    const original = node;
    if (copies.has(original)) return copies.get(original);
    if (depth > MAX_DEPTH || ++nodes > MAX_NODES) {
      stats.truncated += 1;
      return TRUNCATED;
    }

    const source = applyToJSON(node);
    if (typeof source === 'string') return scrubCredentialString(source, stats);
    if (!source || typeof source !== 'object') return source;
    if (!WALKABLE_TAGS.has(typeTagOf(source))) return collapseUnwalkable(source);

    if (Array.isArray(source)) {
      const target: unknown[] = [];
      copies.set(original, target);
      for (const item of source) target.push(walk(item, depth + 1));
      return target;
    }

    const target: Record<string, unknown> = {};
    copies.set(original, target);
    const isAxiosErrorNode = isAxiosError(original);
    for (const key of Object.keys(source)) {
      const child = (source as Record<string, unknown>)[key];
      if (child != null && omitKeys?.has(key)) {
        stats.omitted += 1;
        target[key] = OMITTED;
      } else if (canHoldCredential(child) && isCredentialKey(key)) {
        stats.redacted += 1;
        target[key] = REDACTED;
      } else {
        const childOmit =
          isAxiosErrorNode && key === 'config' ? AXIOS_CONFIG_OMITTED_KEYS : undefined;
        target[key] = walk(child, depth + 1, childOmit);
      }
    }
    return target;
  };

  return walk(value, 0);
}
